import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";

import Prompt from "lib/components/prompt/Prompt";

import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import { Message } from "utils/feedback/Message";
import {
	AddEvent,
	DeleteEvent,
	Event,
	EventGroup,
	MoveEvent
} from "utils/history/Event";
import History from "utils/history/History";
import { ListInfo } from "utils/list/List";
import TableActionbar from "utils/list/TableActionbar";

import BlueprintLibraryInfo from "core/blueprint/base/BlueprintLibraryInfo";
import {
	BlueprintInfo,
	getBlueprintInfo,
	Right
} from "core/blueprint/base/BlueprintService";
import EditActionbar from "core/blueprint/competencelist/EditActionbar";
import {
	getReportBlueprintContent,
	ReportBlueprint,
	updateReportBlueprintContent
} from "core/blueprint/report/ReportBlueprintService";
import { copy } from "core/field/FieldService";
import FieldTable from "core/field/FieldTable";

function ReportBlueprintView(): JSX.Element {
	const REPORT_BLUEPRINT = "reportBlueprint";

	const navigate = useNavigate();
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();

	const [info, setInfo] = useState<BlueprintInfo>();
	const [content, setContent] = useState<ReportBlueprint>({ fields: [] });
	const [listInfo, setListInfo] = useState<ListInfo<string>>(ListInfo.init());
	const [actionHistory, setActionHistory] = useState(new History(content));
	const [message, setMessage] = useState<Message>();

	useEffect(loadReportBlueprint, [id]);

	useEffect(() => {
		setListInfo(prevInfo =>
			prevInfo.withKeys(content.fields.map(field => field.uuid))
		);
	}, [content]);

	function loadReportBlueprint() {
		if (!id) {
			return;
		}

		const idValue = Number.parseInt(id, 10);
		if (Number.isNaN(idValue)) {
			setMessage({
				severity: "error",
				text: t("invalidDocumentId", { document: REPORT_BLUEPRINT })
			});
			return;
		}

		Promise.all([
			getBlueprintInfo(idValue).then(data => setInfo(data)),
			getReportBlueprintContent(idValue).then(data => {
				setContent(data);
				setActionHistory(new History(data));
			})
		]).catch(onLoadFailure);
	}

	const undo = useCallback((): void => {
		console.assert(actionHistory.hasPrevious());
		if (actionHistory.hasPrevious()) {
			setContent(actionHistory.undo());
		}
	}, [actionHistory]);

	const redo = useCallback((): void => {
		console.assert(actionHistory.hasNext());
		if (actionHistory.hasNext()) {
			setContent(actionHistory.redo());
		}
	}, [actionHistory]);

	function onSaveSuccess(): void {
		setMessage({
			severity: "success",
			text: t("saveDocumentSuccess", { document: REPORT_BLUEPRINT })
		});
		loadReportBlueprint();
	}

	function onLoadFailure(): void {
		setMessage({
			severity: "error",
			text: t("loadDocumentError", { document: REPORT_BLUEPRINT })
		});
	}

	function onSaveFailure(reason: Error): void {
		let errorMessage: string;
		if (reason.message === ERR_INTERNET_DISCONNECTED) {
			errorMessage = t("internetDisconnectedError");
		} else {
			errorMessage = t("saveDocumentFailure", {
				document: REPORT_BLUEPRINT
			});
		}

		setMessage({
			severity: "error",
			text: errorMessage
		});
	}

	const save = useCallback((): Promise<void> => {
		console.assert(actionHistory !== undefined);
		setMessage(undefined);

		if (!actionHistory.hasPrevious() || !id) {
			return Promise.resolve();
		}

		const actions = actionHistory.getActions();
		const objects = actions.flatMap(action => action.patches());

		const idValue = Number.parseInt(id, 10);
		return updateReportBlueprintContent(idValue, objects)
			.then(onSaveSuccess)
			.catch(onSaveFailure);
	}, [actionHistory, id]);

	const close = useCallback((): void => {
		navigate("/blueprints");
	}, [navigate]);

	const updateField = useCallback(
		(event: Event): void => {
			console.assert(actionHistory !== undefined);

			event.applyContext("/fields");
			const action = event.toAction();
			setContent(actionHistory.apply(action));
		},
		[actionHistory]
	);

	const selectFields = useCallback(
		(keys: string[], select: boolean): void => {
			setListInfo(previous => previous.markSelected(keys, select));
		},
		[]
	);

	const handleMoveUp = useCallback((): void => {
		const events: Event[] = [];
		for (const [index, field] of content.fields.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(
					new MoveEvent(`/fields/${index}`, `/fields/${index - 1}`)
				);
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [actionHistory, content.fields, listInfo]);

	const handleMoveDown = useCallback((): void => {
		const events: Event[] = [];
		for (const [index, field] of content.fields.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new MoveEvent(`/fields/${index}`, `/fields/${index + 1}`)
				);
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [actionHistory, content.fields, listInfo]);

	const handleDelete = useCallback((): void => {
		const events: Event[] = [];
		for (const [index, field] of content.fields.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(new DeleteEvent("/fields/" + index, field));
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [actionHistory, content.fields, listInfo]);

	const handleDuplicate = useCallback((): void => {
		const events: Event[] = [];
		let index = content.fields.length;
		for (const field of content.fields.values()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(new AddEvent("/fields/" + index, copy(field)));
				index++;
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [actionHistory, content.fields, listInfo]);

	return (
		<>
			{message && (
				<Alert severity={message.severity}>{message.text}</Alert>
			)}
			<Typography id="title" variant="h1">
				{info?.name}
			</Typography>

			{info &&
				info.sourceBlueprintId !== null &&
				info.sourceBlueprintVersion !== null && (
					<BlueprintLibraryInfo
						id={info.id}
						enabled={info.rights.includes(Right.ADMIN) ?? false}
						blueprintId={info.sourceBlueprintId}
						blueprintVersion={info.sourceBlueprintVersion}
						onUpdate={loadReportBlueprint}
					/>
				)}

			<EditActionbar
				undoEnabled={actionHistory.hasPrevious()}
				redoEnabled={actionHistory.hasNext()}
				saveEnabled={actionHistory.hasPrevious()}
				onUndo={undo}
				onRedo={redo}
				onSave={save}
				onClose={close}
			/>

			<TableActionbar
				listInfo={listInfo}
				onMoveUp={handleMoveUp}
				onMoveDown={handleMoveDown}
				onDuplicate={handleDuplicate}
				onDelete={handleDelete}
			/>
			<FieldTable
				elements={content.fields}
				listInfo={listInfo}
				editable={info?.rights.includes(Right.WRITE) ?? false}
				onSelect={selectFields}
				onChange={updateField}
				onInsert={updateField}
			/>

			<Prompt
				when={actionHistory.hasPrevious()}
				message={t("exitDocumentMessage")}
			/>
		</>
	);
}

export default ReportBlueprintView;
